import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, X } from "lucide-react";
import axios from "axios";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function timeAgo(iso) {
  if (!iso) return "just now";
  const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

/**
 * FOMO Activity Ticker — bottom-left toast showing recent registrations.
 * Pulls from /api/public/registration-stats (masked PII: first_name + org only).
 * Rotates every 8s. Auto-hides on /admin/* and /thank-you.
 */
export default function FomoTicker() {
  const [activity, setActivity] = useState([]);
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const path = window.location.pathname;
    if (path.startsWith("/admin") || path.startsWith("/thank-you")) return;
    if (dismissed) return;

    axios
      .get(`${API}/public/registration-stats`)
      .then((r) => {
        const recent = (r.data?.recent_activity || []).filter((a) => a.first_name && a.organisation);
        if (recent.length === 0) return;
        setActivity(recent);
        // Show first toast after 5 sec
        setTimeout(() => setVisible(true), 5000);
      })
      .catch(() => {});
  }, [dismissed]);

  useEffect(() => {
    if (activity.length === 0 || dismissed) return;
    const tick = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx((i) => (i + 1) % activity.length);
        setVisible(true);
      }, 500);
    }, 8000);
    return () => clearInterval(tick);
  }, [activity, dismissed]);

  if (dismissed || activity.length === 0) return null;
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  if (path.startsWith("/admin") || path.startsWith("/thank-you")) return null;

  const current = activity[idx];

  return (
    <AnimatePresence>
      {visible && current && (
        <motion.div
          initial={{ opacity: 0, x: -20, y: 0 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="fixed bottom-4 left-4 z-40 max-w-xs md:max-w-sm hidden md:block"
          data-testid="fomo-ticker"
        >
          <Link
            to="/register"
            className="block bg-white border border-forest-core/15 shadow-lg rounded-[12px] pl-4 pr-8 py-3 hover:border-forest-core/40 transition-colors group relative"
          >
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDismissed(true);
              }}
              className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-100 transition-colors"
              data-testid="fomo-dismiss-btn"
              aria-label="Dismiss"
            >
              <X size={12} className="text-gray-400" />
            </button>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-emerald-100 flex-shrink-0 flex items-center justify-center">
                <CheckCircle2 size={16} className="text-emerald-600" />
              </div>
              <div className="leading-tight">
                <p className="text-forest-core text-xs font-semibold">
                  <span className="font-bold">{current.first_name}</span> from <span className="font-bold">{current.organisation}</span>
                </p>
                <p className="text-muted-text text-[11px]">claimed their free pass · {timeAgo(current.created_at)}</p>
              </div>
            </div>
          </Link>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
